import React from 'react';
import { Box, Button } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { COLORS } from '@/theme/colors';
import { hasUserAccessTo } from '@/lib/permissions';
import { PermissionArea } from '@cloudcost/shared-types/users';
import { SystemData, PendingSystemBudgetChange } from '../../calculations';
import { getEffectiveBudgetValue, getEffectiveBudgetCurrency, calculateShareInDepartmentBudget } from '../../calculations';
import { formatCurrencyNoUnit } from '@/lib/formatters';

interface SystemsColumnsProps {
  pendingBudgetChanges: Map<number, PendingSystemBudgetChange>;
  departmentBudgets: Map<number, { value: number; currency: string; name: string }>;
  onEditSystem: (system: SystemData) => void;
  onSetBudget: (system: SystemData) => void;
  canSetSystemBudget: (system: SystemData) => boolean;
}

export const getSystemsColumns = ({
  pendingBudgetChanges,
  departmentBudgets,
  onEditSystem,
  onSetBudget,
  canSetSystemBudget,
}: SystemsColumnsProps): GridColDef[] => [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
  },
  {
    field: 'owner',
    headerName: 'System Owner',
    flex: 1,
  },
  {
    field: 'value',
    headerName: 'Annual System Budget',
    flex: 1,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: any) => {
      const effectiveValue = getEffectiveBudgetValue(params.row, pendingBudgetChanges);
      const effectiveCurrency = getEffectiveBudgetCurrency(params.row, pendingBudgetChanges);
      const hasPendingChange = pendingBudgetChanges.has(params.row.id);

      if (!effectiveValue && !params.row.value) {
        return <Box sx={{ color: '#E23D3D' }}>No budget set</Box>;
      }

      return (
        <Box
          sx={{
            color: hasPendingChange ? COLORS.primary : 'inherit',
            fontWeight: hasPendingChange ? 'bold' : 'normal',
          }}>
          {effectiveValue !== undefined ? formatCurrencyNoUnit(Number(effectiveValue)): '0.00'} {effectiveCurrency}
          {hasPendingChange && ' *'}
        </Box>
      );
    },
  },
  {
    field: 'share',
    headerName: 'Share in Department Budget',
    flex: 1,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: any) => {
      const sharePercentage = calculateShareInDepartmentBudget(
        params.row, 
        departmentBudgets, 
        pendingBudgetChanges
      );
      const hasPendingChange = pendingBudgetChanges.has(params.row.id);

      return (
        <Box
          sx={{
            color: hasPendingChange ? COLORS.primary : 'inherit',
            fontWeight: hasPendingChange ? 'bold' : 'normal',
          }}>
          {sharePercentage}
          {hasPendingChange && ' *'}
        </Box>
      );
    },
  },
  {
    field: 'actions',
    headerName: 'Actions',
    flex: 1.2,
    sortable: false,
    renderCell: (params: any) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        {hasUserAccessTo(PermissionArea.SYSTEM_EDIT) && (
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEditSystem(params.row);
            }}
            data-testid="system-row-edit">
            Edit
          </Button>
        )}
        {canSetSystemBudget(params.row) && (
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onSetBudget(params.row);
            }}
            data-testid="system-row-set-budget">
            {getEffectiveBudgetValue(params.row, pendingBudgetChanges) || params.row.value
              ? 'Change Budget'
              : 'Set Budget'}
          </Button>
        )}
      </Box>
    ),
  },
];